import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, ChevronRight, Info, CheckCircle } from 'lucide-react';
import Modal from '../components/Modal';
import { toast } from '../components/Toast';
import api from '../api';
import { getApiRobotModels } from '../config/robotModels1';

const robotModels = getApiRobotModels();

const toMediaStatus = (status: 'uploaded' | 'processing' | 'done' | 'failed'): 'pending' | 'running' | 'success' | 'failed' | 'deleted' => {
  switch (status) {
    case 'uploaded':
      return 'pending';
    case 'processing':
      return 'running';
    case 'done':
      return 'success';
    case 'failed':
      return 'failed';
    default:
      return 'pending';
  }
};

const readVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration || 0);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    video.src = url;
  });

export default function CreateDance() {
  const navigate = useNavigate();
  const [robotModel, setRobotModel] = useState(robotModels[0]);
  const [danceName, setDanceName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [mediaId, setMediaId] = useState('');
  const [mediaUploadUrl, setMediaUploadUrl] = useState('');

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);

  const fmtDur = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleVideoSelect = async (file: File) => {
    setVideoFile(file);
    setMediaId('');
    setMediaUploadUrl('');
    setVideoDuration(0);
    void readVideoDuration(file).then(setVideoDuration);

    try {
      const uploadInfo = await api.media.createUploadUrl(file.name);
      setMediaId(uploadInfo.media_id);
      setMediaUploadUrl(uploadInfo.upload_url);
    } catch {
      toast('error', '获取上传地址失败');
    }
  };

  const handleUploadToUrl = async (): Promise<boolean> => {
    if (!videoFile || !mediaUploadUrl) return false;

    try {
      await api.uploadToUrl(mediaUploadUrl, videoFile);
      return true;
    } catch {
      toast('error', '视频上传失败');
      return false;
    }
  };

  const handleUpdateMedia = async (): Promise<boolean> => {
    if (!mediaId) return false;

    try {
      // 时长是异步读取的，可能还没就绪（state 仍为 0）；这里当场再读一次，确保不为 0
      let dur = videoDuration;
      if (dur <= 0 && videoFile) {
        dur = await readVideoDuration(videoFile);
      }
      await api.media.updateMedia(mediaId, {
        media_name: videoFile?.name || danceName,
        duration: dur > 0 ? Math.round(dur) : undefined,
        status: toMediaStatus('done'),
      });
      return true;
    } catch {
      toast('error', '更新媒体信息失败');
      return false;
    }
  };

  const handleCreateSimulation = async (): Promise<boolean> => {
    if (!mediaId) return false;

    try {
      await api.simulation.create({
        platform: 'edit',
        media_id: mediaId,
        simulation_name: danceName,
        robot_model: robotModel,
      });
      return true;
    } catch {
      toast('error', '创建仿真失败');
      return false;
    }
  };

  const handleNext = () => {
    if (!danceName.trim()) { toast('warning', '请输入舞蹈名称'); return; }
    if (!videoFile) { toast('warning', '请上传视频'); return; }
    setShowConfirmModal(true);
  };

  const handleConfirmGenerate = async () => {
    setShowConfirmModal(false);

    const uploaded = await handleUploadToUrl();
    if (!uploaded) return;
    const updated = await handleUpdateMedia();
    if (!updated) return;

    const created = await handleCreateSimulation();
    if (!created) return;

    setShowSuccessModal(true);
  };

  const handleViewDances = () => { setShowSuccessModal(false); navigate('/my-dances'); };

  const displayDuration = videoDuration > 0 ? fmtDur(Math.round(videoDuration)) : '--:--';

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-8"><h1 className="text-2xl font-bold text-text-primary font-display">创建舞蹈</h1><p className="text-sm text-text-secondary mt-1.5">上传视频生成舞蹈动作</p></div>

      <div className="bg-white rounded-2xl shadow-soft p-8 border border-border-subtle">
        <div className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer mb-8 relative ${isDragging ? 'border-brand bg-brand-50' : 'border-slate-200 hover:border-brand/40 hover:bg-slate-50'}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files && e.dataTransfer.files[0]) { void handleVideoSelect(e.dataTransfer.files[0]); } }}
          onClick={() => document.getElementById('video-upload')?.click()}>
          <input type="file" id="video-upload" accept="video/mp4,video/quicktime" className="hidden" onChange={(e) => { if (e.target.files && e.target.files[0]) { void handleVideoSelect(e.target.files[0]); } }} />
          {videoFile ? (
            <div className="flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-success-50 rounded-2xl flex items-center justify-center mx-auto mb-4"><CheckCircle size={28} className="text-success" /></div>
              <p className="text-sm text-text-primary font-semibold mb-1">{videoFile.name}</p>
              <p className="text-xs text-text-muted mt-2">点击或拖拽更换视频</p>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4"><Upload size={28} className="text-brand" /></div>
              <p className="text-sm text-text-primary font-semibold mb-1">拖拽或点击上传视频</p>
              <p className="text-xs text-text-muted">MP4 / MOV，15-60s</p>
              <div className="mt-5 bg-slate-50 rounded-xl p-4 text-left max-w-sm mx-auto border border-border-subtle">
                <p className="text-xs text-text-secondary font-medium mb-2 flex items-center gap-1.5"><Info size={13} /> 视频建议：</p>
                <ul className="text-xs text-text-muted space-y-1 list-disc list-inside"><li>真人舞蹈，正对镜头，保持稳定</li><li>从双手自然下垂开始</li><li>光线明亮，背景简洁</li></ul>
              </div>
            </>
          )}
        </div>

        <div className="mb-5">
          <label className="text-sm font-semibold text-text-primary block mb-2">机器人型号</label>
          <select value={robotModel} onChange={(e) => setRobotModel(e.target.value)} className="w-full px-4 py-3 border border-border-main rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand">
            {robotModels.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="mb-8">
          <label className="text-sm font-semibold text-text-primary block mb-2">舞蹈名称</label>
          <input type="text" value={danceName} onChange={(e) => setDanceName(e.target.value.slice(0, 20))} placeholder="请输入舞蹈名称" maxLength={20}
            className="w-full px-4 py-3 border border-border-main rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" />
          <p className="text-xs text-text-muted mt-1.5 text-right">{danceName.length}/20</p>
        </div>
        <button onClick={handleNext} disabled={!danceName.trim()}
          className="w-full py-3.5 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          下一步 <ChevronRight size={16} />
        </button>
      </div>

      {/* 确认生成信息 */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="生成舞蹈" width="max-w-sm">
        <p className="text-xs text-text-muted mb-4 text-center">请再次确认以下信息</p>
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">舞蹈名称</span>
            <span className="font-semibold text-text-primary">{danceName}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">机器人型号</span>
            <span className="font-semibold text-text-primary">{robotModel}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">舞蹈时长</span>
            <span className="font-semibold text-text-primary">{displayDuration}</span>
          </div>
        </div>
        <div className="bg-brand-50 rounded-xl px-4 py-3 border border-brand/10 mb-5 text-center">
          <p className="text-sm font-semibold text-brand-dark">生成舞蹈视频</p>
          <p className="text-xs text-text-muted mt-0.5">包含：仿真视频</p>
        </div>
        <button onClick={handleConfirmGenerate} className="w-full py-3.5 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift">
          确认生成
        </button>
      </Modal>

      {/* 生成成功 */}
      <Modal isOpen={showSuccessModal} onClose={handleViewDances} title="生成成功" width="max-w-sm" hideClose>
        <div className="text-center py-6">
          <div className="w-20 h-20 bg-success-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={40} className="text-success" />
          </div>
          <h3 className="text-lg font-bold text-text-primary mb-2 font-display">提交成功！</h3>
          <p className="text-sm text-text-muted mb-1">舞蹈「{danceName}」已提交生成</p>
          <p className="text-xs text-text-muted">您可以在「我的舞蹈」中查看生成进度</p>
          <button onClick={handleViewDances} className="mt-6 px-8 py-3 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift">
            查看我的舞蹈
          </button>
        </div>
      </Modal>
    </div>
  );
}
